import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatoMXN, formatoFecha, ESTADOS } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Phone, Mail, MapPin, Car, ClipboardList, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function ClienteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await api.get(`/clientes/${id}`);
        setCliente(res.data);
      } catch { toast.error('Error al cargar cliente'); navigate('/clientes'); }
      finally { setCargando(false); }
    };
    cargar();
  }, [id, navigate]);

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-48 bg-slate-800" /></div>;
  if (!cliente) return null;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="cliente-detalle-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/clientes')} className="text-slate-400 hover:text-white" data-testid="volver-clientes-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">{cliente.nombre_completo}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Informacion</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {cliente.telefono && <div className="flex items-center gap-3 text-sm"><Phone className="w-4 h-4 text-slate-500" /><span className="text-slate-300">{cliente.telefono}</span></div>}
            {cliente.email && <div className="flex items-center gap-3 text-sm"><Mail className="w-4 h-4 text-slate-500" /><span className="text-slate-300">{cliente.email}</span></div>}
            {cliente.direccion && <div className="flex items-center gap-3 text-sm"><MapPin className="w-4 h-4 text-slate-500" /><span className="text-slate-300">{cliente.direccion}</span></div>}
            {cliente.notas && <div className="mt-3 p-3 bg-slate-800/50 rounded-sm text-sm text-slate-400">{cliente.notas}</div>}
            <p className="text-xs text-slate-600 mt-3">Registrado: {formatoFecha(cliente.created_at)}</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/40 border-slate-800 lg:col-span-2">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2"><Car className="w-5 h-5 text-amber-500" /> Vehiculos</CardTitle>
            <Button size="sm" onClick={() => navigate(`/vehiculos?nuevo=true&cliente_id=${id}`)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm text-xs" data-testid="agregar-vehiculo-btn">
              <Plus className="w-3 h-3 mr-1" /> Agregar
            </Button>
          </CardHeader>
          <CardContent>
            {cliente.vehiculos?.length > 0 ? (
              <div className="space-y-2">
                {cliente.vehiculos.map(v => (
                  <div key={v.id} className="flex items-center justify-between p-3 bg-slate-800/40 rounded-sm border border-slate-800 hover:border-slate-700 cursor-pointer transition-colors"
                    onClick={() => navigate(`/vehiculos/${v.id}`)} data-testid={`vehiculo-item-${v.id}`}>
                    <div>
                      <p className="text-sm text-slate-200 font-medium">{v.marca} {v.modelo} {v.anio}</p>
                      <p className="text-xs font-mono text-amber-500">{v.placas}</p>
                    </div>
                    <span className="text-xs text-slate-500">{v.color}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500 text-center py-6">No hay vehiculos registrados</p>}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-500" /> Órdenes de Trabajo</CardTitle>
          <Button size="sm" onClick={() => navigate(`/ordenes/nueva?cliente_id=${id}`)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm text-xs" data-testid="nueva-orden-cliente-btn">
            <Plus className="w-3 h-3 mr-1" /> Nueva Orden
          </Button>
        </CardHeader>
        <CardContent>
          {cliente.ordenes?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Orden</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Problema</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Estado</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Total</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cliente.ordenes.map(o => (
                  <TableRow key={o.id} className="border-slate-800/50 cursor-pointer hover:bg-slate-800/40"
                    onClick={() => navigate(`/ordenes/${o.id}`)} data-testid={`orden-cliente-${o.id}`}>
                    <TableCell className="font-mono text-xs text-amber-500">{o.numero_orden}</TableCell>
                    <TableCell className="text-sm text-slate-300 max-w-[200px] truncate">{o.problema_reportado}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold ${ESTADOS[o.estado]?.clase}`}>
                        {ESTADOS[o.estado]?.label || o.estado}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-300 text-right">{formatoMXN(o.total)}</TableCell>
                    <TableCell className="text-sm text-right">
                      <span className={o.saldo_pendiente > 0 ? 'text-orange-400' : 'text-green-400'}>
                        {formatoMXN(o.saldo_pendiente)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <p className="text-sm text-slate-500 text-center py-6">No hay ordenes de trabajo</p>}
        </CardContent>
      </Card>
    </div>
  );
}
